<template>
  <div class="products-list-simple">
    <LoadingBlock :loading="productsLoading">
      <div v-if="!productsLoading && (by === 'filters' ? products.length === 0 : allProducts.length === 0)" class="text-center my-3">
        Товары не найдены
      </div>
      <div class="table-responsive" v-show="(by === 'filters' ? products.length > 0 : allProducts.length > 0) || productsLoading">
        <table class="table table-sm table-bordered">
          <thead>
            <tr>
              <th v-if="with_checkbox"></th>
              <th>Название</th>
              <th v-for="column in columns" :key="column.key">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, index) in paginatedProducts" :key="index">
              <td v-if="with_checkbox" @click="toggleSelectProduct(product.id)" style="cursor:pointer">
                <input type="checkbox" class="form-check-input" :checked="selectedIds.includes(product.id)">
              </td>
              <td>{{ product.name }}</td>
              <td v-for="column in columns" :key="column.key">
                <slot :name="`column-${column.key}`" :product="product"></slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="row">
        <div class="col-sm-4">
          <div class="mt-3" v-show="(by === 'filters' ? products.length > 0 : allProducts.length > 0)">
            Товаров на странице: <Select v-model="pageSize" :options="pageSizes" @change="getProducts">
            </Select>
          </div>
        </div>
        <div class="col-sm-4">
          <BasePagination :info="paginationInfo" ref="productsPagination" @changePage="changePage">
          </BasePagination>
        </div>
      </div>
    </LoadingBlock>
  </div>
</template>

<script>
import { getProductsBulkInfo, getProductsPost } from "@/api/product";
import { user } from "@/data/user";
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import BasePagination from "@/components/list/Pagination.vue";
import { Select } from "primevue";


export default {
  name: 'ProductsListSimple',
  components: { BasePagination, LoadingBlock, Select },
  data() {
    return {
      products: [],
      allProducts: [],
      productsLoading: false,
      paginationInfo: {},
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      selectedIds: [],
      abortController: null
    }
  },
  computed: {
    paginatedProducts() {
      if (this.by === 'filters') {
        return this.products
      }
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.allProducts.slice(start, end)
    }
  },
  methods: {
    async getProducts() {
      if (this.by === 'filters') {
        await this.getProductsByFilters()
      } else {
        await this.getProductsByIds()
      }
    },
    async getProductsByIds() {
      if (!this.product_ids || this.product_ids.length === 0) {
        this.products = []
        this.allProducts = []
        this.productsLoading = false
        this.updatePaginationInfo()
        return
      }
      this.productsLoading = true
      this.allProducts = [] // Инициализируем пустым массивом
      try {
        const company_id = await user.getActiveCompany()
        const result = await getProductsBulkInfo(this.product_ids, company_id)
        this.allProducts = result.data.products
        this.updatePaginationInfo()
      } catch (error) {
        console.error('Error fetching products:', error)
        // Fallback to basic product info if API fails
        this.allProducts = this.product_ids.map(id => ({
          id: id,
          name: id.toString()
        }))
        this.updatePaginationInfo()
      }
      this.productsLoading = false
    },
    updatePaginationInfo() {
      const total = this.allProducts?.length || 0
      this.paginationInfo = {
        total: total,
        pages: Math.ceil(total / this.pageSize) || 1,
        page: this.currentPage,
        size: this.pageSize
      }
    },
    async getProductsByFilters() {
      // Отменяем предыдущий запрос, если он есть
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.productsLoading = true
      const filters = JSON.parse(JSON.stringify(this.filters))
      filters.page = this.currentPage;
      filters.size = this.pageSize;
      filters.company = await user.getActiveCompany();
      try {
        const result = await getProductsPost(filters, { signal: this.abortController.signal })
        this.paginationInfo = {
          total: result.data.total,
          pages: result.data.pages,
          page: result.data.page,
          size: result.data.size
        }
        this.currentPage = result.data.page
        this.pageSize = result.data.size
        this.products = []
        this.products = result.data.items
      } catch (error) {
        if (error.name === 'AbortError') {
          // Запрос был отменен, игнорируем ошибку
          return;
        }
        console.error('Error fetching products:', error);
      } finally {
        this.productsLoading = false;
      }
    },
    toggleSelectProduct(id) {
      if (this.selectedIds.includes(id)) {
        this.selectedIds = this.selectedIds.filter(item => item !== id);
      } else {
        this.selectedIds = [...this.selectedIds, id];
      }
      this.$emit('selection-changed', this.selectedIds);
    },
    changePage(page) {
      this.currentPage = page;
      if (this.by === 'filters') {
        this.getProducts();
      } else {
        this.updatePaginationInfo();
      }
    },
    refreshProducts() {
      this.currentPage = 1;
      this.getProducts();
    },
    reset() {
      this.selectedIds = []
      this.currentPage = 1
      this.pageSize = 10
      this.products = []
      this.allProducts = []
      this.productsLoading = false
      this.paginationInfo = {}
    }
  },
  watch: {
    filters: {
      handler() {
        this.refreshProducts()
      },
      deep: true
    },
    product_ids: {
      handler() {
        this.refreshProducts()
      },
      deep: true
    },
    pageSize() {
      this.updatePaginationInfo()
    }
  },
  emits: ['selection-changed'],
  props: {
    by: {
      type: String,
      default: 'filters'
    },
    filters: {
      type: Object
    },
    product_ids: {
      type: Array,
      default: () => []
    },
    with_checkbox: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every(col => 'key' in col && 'label' in col);
      }
    },
    width: {
      type: Number,
      default: 400
    }
  },
  async mounted() {
    await user.getUserBase()
  }
}
</script>